/**
 * Trigger Evaluator
 *
 * Determines whether a notification template's automation trigger matches
 * a given resource event. Supports: created, updated, changed-to,
 * changed-from, changed-from-to.
 */
import type { NotificationAutomationTrigger, TriggerChangeType } from '../notification-automation.types';

/**
 * Safely resolve a nested field value from an object using dot-notation path.
 */
export function resolveFieldPath(
  obj: Record<string, unknown> | undefined,
  path: string,
): unknown {
  if (!obj || !path) return undefined;
  const segments = path.split('.');
  let current: unknown = obj;
  for (const segment of segments) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function valuesMatch(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return String(a) === String(b);
}

export interface TriggerEvaluationInput {
  changeType: TriggerChangeType;
  currentResource: Record<string, unknown>;
  previousResource?: Record<string, unknown>;
  isNew: boolean;
}

/**
 * Evaluate whether a trigger config matches the given event.
 */
export function evaluateTrigger(
  triggerConfig: NotificationAutomationTrigger,
  input: TriggerEvaluationInput,
): boolean {
  switch (triggerConfig.changeType) {
    case 'created':
      return input.isNew;

    case 'updated':
      return !input.isNew;

    case 'changed-to': {
      if (!input.previousResource) return false;
      const currentVal = resolveFieldPath(input.currentResource, triggerConfig.fieldPath);
      const prevVal = resolveFieldPath(input.previousResource, triggerConfig.fieldPath);
      return (
        valuesMatch(currentVal, triggerConfig.toValue) &&
        !valuesMatch(prevVal, triggerConfig.toValue)
      );
    }

    case 'changed-from': {
      if (!input.previousResource) return false;
      const currentVal = resolveFieldPath(input.currentResource, triggerConfig.fieldPath);
      const prevVal = resolveFieldPath(input.previousResource, triggerConfig.fieldPath);
      return (
        valuesMatch(prevVal, triggerConfig.fromValue) &&
        !valuesMatch(currentVal, triggerConfig.fromValue)
      );
    }

    case 'changed-from-to': {
      if (!input.previousResource) return false;
      const currentVal = resolveFieldPath(input.currentResource, triggerConfig.fieldPath);
      const prevVal = resolveFieldPath(input.previousResource, triggerConfig.fieldPath);
      return (
        valuesMatch(prevVal, triggerConfig.fromValue) &&
        valuesMatch(currentVal, triggerConfig.toValue)
      );
    }

    default:
      return false;
  }
}
